import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import {
  CellDoubleClickedEvent,
  CellEditingStartedEvent,
  GetContextMenuItems,
  MenuItemDef,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, sideBar } from '@/grid/defaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { PrimaryContext } from '@/services';
import { useSelectedSemenInventoryReport } from '@/services/reports';
import { SemenDto } from '@/services/semen-service/types';
import { ToastLife } from '@/services/toast';
import { useContextMenu } from '@/utils/context-menu';

import { useSemenAppContext } from '../../context/SemenAppContext';
import { useGridSelection } from '../../hooks';
import { useHomeTabSemenColumnDefs } from '../../util/column-defs';
import { useSemenDataSource } from '../../util/semen-data-source';
import { SemenSelector } from './SemenSelector';

export const HomeTab = () => {
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { themeMode } = useContext(PrimaryContext);
  const isSemenDataPointsEnabled = useFeatureFlag('Semen_Data_Points');
  const isPersistSemenAcrossTabsEnabled = useFeatureFlag(
    'Open_New_Shipment_in_New_Window',
  );
  const isSemenInventoryReportEnabled = useFeatureFlag(
    'Semen_Inventory_Report',
  );
  const {
    selectedSemen,
    selectedSemenRef,
    setSelectedSemen,
    filters,
    setLoading,
    homeTabGridRef: gridRef,
    refreshHomeTabGrid: refreshGridWithFilters,
  } = useSemenAppContext();

  const { getContextMenuItems: defaultContextMenu } = useContextMenu({
    idKey: 'id',
  });
  const [generateSemenInventory, semenInventoryReport] =
    useSelectedSemenInventoryReport();

  const getContextMenuItems = useCallback<GetContextMenuItems>(
    (params) => {
      const defaultItems = defaultContextMenu(params);
      if (!isSemenInventoryReportEnabled) return defaultItems;

      const selectedRows = params.api.getSelectedRows() as SemenDto[];
      const clickedSemen = params.node?.data as SemenDto | undefined;
      const rows =
        params.node?.isSelected() && selectedRows.length > 0
          ? selectedRows
          : clickedSemen
            ? [clickedSemen]
            : selectedRows;

      if (rows.length === 0) return defaultItems;

      const printReportItem: MenuItemDef = {
        name: `Print Semen Inventory Report (${rows.length})`,
        icon: '<i class="pi pi-print" />',
        disabled: semenInventoryReport.isPending,
        action: () =>
          generateSemenInventory({
            semenIds: rows.map((semen) => semen.id),
          }),
      };

      const [viewHistoryItem, ...remainingItems] = defaultItems;
      return [
        viewHistoryItem,
        printReportItem,
        'separator',
        ...remainingItems.filter(
          (item, index) => item !== 'separator' || index > 0,
        ),
      ];
    },
    [
      defaultContextMenu,
      generateSemenInventory,
      isSemenInventoryReportEnabled,
      semenInventoryReport.isPending,
    ],
  );

  const isRestoringSelectionRef = useRef(false);

  const handleSelectionChange = useCallback(
    (rows: SemenDto[]) => {
      if (isRestoringSelectionRef.current) return;
      setSelectedSemen(rows);
    },
    [setSelectedSemen],
  );

  const { dataSource } = useSemenDataSource(
    filters,
    setLoading,
    isSemenDataPointsEnabled,
  );

  useEffect(() => {
    refreshGridWithFilters();
  }, [filters, refreshGridWithFilters]);

  const { handleSelectionChanged } = useGridSelection<SemenDto>({
    gridRef,
    onSelectionChange: isPersistSemenAcrossTabsEnabled
      ? handleSelectionChange
      : setSelectedSemen,
    mode: 'multiple',
  });

  const restoreSelectionFromContext = useCallback(() => {
    const api = gridRef?.current?.api;
    if (!api) return;
    const byId = new Map(selectedSemen.map((s) => [String(s.id), s]));
    if (byId.size === 0) return;
    isRestoringSelectionRef.current = true;
    api.forEachNode((node) => {
      const match = node.data ? byId.get(String(node.data.id)) : undefined;
      if (match) {
        node.setSelected(true);
        if (match.numberOfStrawsToAdd != null) {
          node.setDataValue('numberOfStrawsToAdd', match.numberOfStrawsToAdd);
        }
      }
    });
    isRestoringSelectionRef.current = false;
  }, [gridRef, selectedSemen]);

  const firstSelectedSemen = useMemo(
    () => selectedSemen[0] || null,
    [selectedSemen],
  );

  const totalReceived = useMemo(
    () => Number(firstSelectedSemen?.quantityReceived ?? 0),
    [firstSelectedSemen],
  );
  const scheduled = useMemo(
    () =>
      Number(firstSelectedSemen?.pulledSemenQuantity ?? 0) +
      Number(firstSelectedSemen?.quantityToShip ?? 0),
    [firstSelectedSemen],
  );

  const available = useMemo(
    () => Number(firstSelectedSemen?.quantity ?? 0),
    [firstSelectedSemen],
  );

  const onCellDoubleClicked = (event: CellDoubleClickedEvent<SemenDto>) => {
    if (event.colDef?.field === 'numberOfStrawsToAdd') return;
    if (event.data) {
      setSelectedSemen([event.data]);
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('semenId', event.data.id);
      const url = `/semen/detail?${urlParams.toString()}`;
      window.open(url, '_blank');
    }
  };

  const onSemenCellValueChanged = (event: any) => {
    if (event.colDef?.field !== 'numberOfStrawsToAdd') {
      return;
    }

    const semen = event.data;
    if (semen?.id) {
      const newValue = Number(event.value) ?? 0;
      const quantity = Number(semen.quantity) || 0;

      if (quantity === 0) {
        if (newValue !== 0) {
          event.node.setDataValue(event.colDef.field, null);

          setSelectedSemen(
            selectedSemenRef.current.map((s) =>
              s.id === semen.id
                ? { ...s, numberOfStrawsToAdd: null as any }
                : s,
            ),
          );

          dispatch(
            addToast({
              severity: 'warn',
              summary: t('common.validation'),
              detail: t('common.error'),
              life: ToastLife.DEFAULT,
            }),
          );
        }
        return;
      }

      if (newValue > quantity) {
        if (newValue !== null) {
          event.node.setDataValue(event.colDef.field, null);
          setSelectedSemen(
            selectedSemenRef.current.map((s) =>
              s.id === semen.id
                ? { ...s, numberOfStrawsToAdd: null as any }
                : s,
            ),
          );

          dispatch(
            addToast({
              severity: 'warn',
              summary: t('common.validation'),
              detail: t('common.error'),
              life: ToastLife.DEFAULT,
            }),
          );
        }
        return;
      }

      setSelectedSemen(
        selectedSemenRef.current.map((s) =>
          s.id === semen.id ? { ...s, numberOfStrawsToAdd: newValue } : s,
        ),
      );
    }
  };

  const onCellEditingStarted = (event: CellEditingStartedEvent) => {
    if (event.colDef?.field === 'numberOfStrawsToAdd') {
      event.node.setSelected(true);
    }
  };

  const columnDefs = useHomeTabSemenColumnDefs();

  return (
    <div className="home-tab">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex h-[calc(100vh-220px)] flex-col lg:col-span-2">
          <SemenSelector gridRef={gridRef} />
          <div className="mt-4 min-h-0 flex-1">
            <WithLocalStorage storageKey="semen-app-grid">
              <AgGridReact
                gridOptions={defaultGridOptions}
                ref={gridRef}
                className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} semen-app`.trim()}
                columnDefs={columnDefs}
                onSelectionChanged={handleSelectionChanged}
                getRowId={(params) => String(params.data.id)}
                onFirstDataRendered={
                  isPersistSemenAcrossTabsEnabled
                    ? restoreSelectionFromContext
                    : undefined
                }
                onCellDoubleClicked={onCellDoubleClicked}
                onFilterChanged={refreshGridWithFilters}
                onSortChanged={refreshGridWithFilters}
                suppressRowClickSelection={true}
                rowSelection="multiple"
                defaultColDef={defaultColDef}
                rowModelType="serverSide"
                serverSideDatasource={dataSource}
                cacheBlockSize={50}
                sideBar={sideBar}
                getContextMenuItems={getContextMenuItems}
                onCellValueChanged={onSemenCellValueChanged}
                onCellEditingStarted={onCellEditingStarted}
              />
            </WithLocalStorage>
          </div>
        </div>
      </div>
    </div>
  );
};
